/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ManagementAccountListInterface,
  ManagementStationInterFace,
  ManagementStationRequestInterface,
  ManagementStationResponseInterface,
} from '../../interface/revenue-management.interface';
import {
  ClearFilters,
  // ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import revenueApi from '../../utils/api/revenue';

import { setNotificationMessage } from './NotificationManagementSlice';

interface ManageStationInitialProperties {
  stations: ManagementStationInterFace[];
  accountList: { label: string; value: string }[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  filter: {};
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  sortBy: 'asc' | 'desc' | null;
  sortField: string | null;
}

// a thunk to fetch accounts
export const fetchAllStations = createAsyncThunk<
  ManagementStationResponseInterface,
  ManagementStationRequestInterface
>(
  'revenue-management-station/fetchAllStations',
  async (data: ManagementStationRequestInterface, { dispatch }) => {
    try {
      const response = await revenueApi.fetchRevenueConfigurationList(
        removeEmptyKeys({ ...data, type: 'Station' }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

// a thunk to refresh stations
export const refreshAllStations = createAsyncThunk<
  ManagementStationResponseInterface,
  ManagementStationRequestInterface
>(
  'revenue-management-station/refreshAllStations',
  async (data: ManagementStationRequestInterface) => {
    const response = await revenueApi.fetchRevenueConfigurationList(
      removeEmptyKeys({ ...data, type: 'Station' }),
    );
    return response.data;
  },
);

// a thunk to fetch devices filters
// export const fetchRevenueManagementStationFilters = createAsyncThunk<
//   CommonFilterProperties[], // Return type
//   void // No need for an argument, since we are not using the payload
// >('revenue-management-station/fetchRevenueManagementStationFilters', async (_, { dispatch }) => {
//   try {
//     const response = await revenueApi.fetchRevenueConfigurationListFilters({ type: 'Station' });
//     const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
//     return updatedResponse;
//   } catch (error: any) {
//     dispatch({
//       type: 'notification/setNotificationDetails',
//       payload: {
//         type: 'error',
//         notificationDetails: {
//           statusCode: error?.response?.data?.code || 200,
//           message: error?.response?.data?.message || 'Something went wrong!',
//         },
//       },
//     });
//     throw error;
//   }
// });

export const fetchAccountList = createAsyncThunk<ManagementAccountListInterface>(
  'revenue-management-station/fetchAccountList',
  async (_, { dispatch }) => {
    try {
      const response = await revenueApi.fetchAccountList();
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ManageStationInitialProperties = {
  stations: [],
  accountList: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

// Create the slice
export const revenueSlice = createSlice({
  name: 'revenue-management-station',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateRevenueManagementStationFilters: (
      state,
      action: PayloadAction<FilterUpdateProperties>,
    ) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state.
     */
    disableRevenueManagementStationFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Set Account filter data function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setRevenueStationFilterData: (state, action) => {
      state.filter = action.payload;
    },
    /**
     * Function to update single station in redux
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateSingleStation: (
      state: ManageStationInitialProperties,
      action: PayloadAction<ManagementStationInterFace>,
    ) => {
      const updatedAccount = action.payload; // station to be added at the top
      const accountList = state.stations; // Assign the existing station to another variable

      // Find the index of the updated station by ID
      const index = accountList.findIndex((eachStation) => eachStation.id === updatedAccount.id);

      // If the index found, replace the entire data
      if (index !== -1) {
        accountList[index] = {
          ...updatedAccount,
          revenuePercentage: action.payload.revenuePercentage,
        };
      }
      state.stations = accountList; // Finally update the station list
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllStations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllStations.fulfilled, (state, action) => {
        state.loading = false;
        state.stations = action.payload?.results?.length
          ? action.payload?.results?.map((result) => ({
              ...result,
              id: result._id,
              stationId: result.chargingStationId,
              name: result.chargingStationName,
              revenuePercentage: result.partnerRevenuePercentage
                ? Number(result.partnerRevenuePercentage)
                : 0,
              partnerRevenuePercentage: result.partnerRevenuePercentage ?? 0,
              cpoRevenuePercentage: result.cpoRevenuePercentage ?? 0,
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchAllStations.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder.addCase(refreshAllStations.fulfilled, (state, action) => {
      state.stations = action.payload?.results?.length
        ? action.payload?.results?.map((result) => ({
            ...result,
            id: result._id,
            stationId: result.chargingStationId,
            name: result.chargingStationName,
            revenuePercentage: result.partnerRevenuePercentage
              ? Number(result.partnerRevenuePercentage)
              : 0,
            partnerRevenuePercentage: result.partnerRevenuePercentage ?? 0,
            cpoRevenuePercentage: result.cpoRevenuePercentage ?? 0,
          }))
        : [];
      state.limit = action.payload.limit;
      state.page = action.payload.page;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    });
    // builder
    //   .addCase(fetchRevenueManagementStationFilters.pending, (state) => {
    //     state.filterListLoading = true;
    //   })
    //   .addCase(
    //     fetchRevenueManagementStationFilters.fulfilled,
    //     (state, action: PayloadAction<CommonFilterProperties[]>) => {
    //       state.filterListLoading = false;
    //       state.filters = [...(state.filters || []), ...action.payload];
    //     },
    //   )
    //   .addCase(fetchRevenueManagementStationFilters.rejected, (state) => {
    //     state.filterListLoading = false;
    //   });
  },
});

// Export the actions
export const {
  setRevenueStationFilterData,
  updateSingleStation,
  updateRevenueManagementStationFilters,
  disableRevenueManagementStationFilterApplied,
  setSortData,
} = revenueSlice.actions;

// Export the reducer
export default revenueSlice.reducer;
